<script setup>
import EmojiOrIcon from 'shared/components/EmojiOrIcon.vue';
import { computed } from 'vue';

const props = defineProps({
  title: {
    type: String,
    required: true,
  },
  compact: {
    type: Boolean,
    default: false,
  },
  icon: {
    type: String,
    default: '',
  },
  emoji: {
    type: String,
    default: '',
  },
  isOpen: {
    type: Boolean,
    default: true,
  },
  // When false, the section is permanently expanded and the collapse/expand
  // control is hidden. Used by the Zimobi contact/conversation sidebars so the
  // sections behave the same on desktop as they do on mobile.
  collapsible: {
    type: Boolean,
    default: true,
  },
  // When false, the header is not a drag handle, so the grab cursor is not
  // shown. Used by sidebars that keep a fixed section order.
  draggable: {
    type: Boolean,
    default: true,
  },
});

const emit = defineEmits(['toggle']);

// A non-collapsible section is always shown expanded, regardless of `isOpen`.
const isExpanded = computed(() => !props.collapsible || props.isOpen);

const headerCursor = computed(() => {
  if (props.draggable) return 'cursor-grab';
  return props.collapsible ? 'cursor-pointer' : 'cursor-default';
});

const onToggle = () => {
  if (!props.collapsible) return;
  emit('toggle');
};
</script>

<template>
  <div class="text-sm">
    <button
      class="flex items-center select-none w-full rounded-lg bg-n-slate-2 outline outline-1 outline-n-weak m-0 justify-between py-2 px-4 drag-handle"
      :class="[{ 'rounded-bl-none rounded-br-none': isExpanded }, headerCursor]"
      @click.stop="onToggle"
    >
      <div class="flex justify-between">
        <EmojiOrIcon class="inline-block w-5" :icon="icon" :emoji="emoji" />
        <h5 class="text-n-slate-12 text-sm mb-0 py-0 pr-2 pl-0">
          {{ title }}
        </h5>
      </div>
      <div class="flex flex-row">
        <slot name="button" />
        <div
          v-if="collapsible"
          class="hidden sm:flex justify-end w-3 text-n-blue-11 cursor-pointer"
        >
          <fluent-icon
            v-if="isExpanded"
            size="24"
            icon="subtract"
            type="solid"
          />
          <fluent-icon v-else size="24" icon="add" type="solid" />
        </div>
      </div>
    </button>
    <div
      v-if="isExpanded"
      class="outline outline-1 outline-n-weak -mt-[-1px] border-t-0 rounded-br-lg rounded-bl-lg"
      :class="compact ? 'p-0' : 'px-2 py-4'"
    >
      <slot />
    </div>
  </div>
</template>
